import { readerBrand } from '@lib/customization/reportBranding';
import { useGetReportBrandingLogoQuery, useGetReportBrandingsQuery } from '@store/api/reportBrandingEndpoints';
import { type ReportText } from '@store/api/reportDesignEndpoints';
import { useAppSelector, usePermission } from '@store/hooks';
import { useMemo } from 'react';

interface Brand {
  brandName?: ReportText;
  /** The logo as an address a picture can show; absent while it is read, and where there is none. */
  logo?: string;
  /** A logo is set and is on its way. */
  logoReading: boolean;
}

/**
 * The brand the reader's own reports carry, for what a design's theme leaves unset: a report
 * drawn with the design prints that brand's logo and name there, and so does the canvas.
 *
 * Put together here from the layers the reader may read, which is every author's own team
 * and the organization. A reader who may read no layer gets none, and the canvas then shows
 * the product's where their report would show a brand: the PDF is the report.
 */
export const useReaderBrand = (): Brand => {
  const { canInAnyTeam } = usePermission();
  const defaultTeamId = useAppSelector((state) => state.auth.user?.defaultTeam?.id);

  const { data: layers } = useGetReportBrandingsQuery(undefined, { skip: !canInAnyTeam('report_branding:get') });
  const brand = useMemo(() => readerBrand(layers, defaultTeamId), [layers, defaultTeamId]);

  // `currentData`, not `data`: after the logo is replaced, `data` is still the one shown before.
  const { currentData: logo, isFetching } = useGetReportBrandingLogoQuery(
    { teamId: brand.logoOwner, imageId: brand.logo?.imageId ?? '' },
    { skip: !brand.logo },
  );

  return useMemo(
    () => ({ brandName: brand.brandName, logo: brand.logo ? logo : undefined, logoReading: Boolean(brand.logo) && isFetching }),
    [brand, logo, isFetching],
  );
};
